import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login, logout } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    if (r.user.role === "admin") {
      // Admin must sign in from the dedicated admin URL, not the public portal.
      await logout();
      setErr("This account cannot sign in from the student portal.");
      return;
    }
    toast.success(`Welcome back, ${r.user.name}!`);
    nav("/app");
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative overflow-hidden royal-gradient grain">
        <div className="absolute -bottom-20 -left-10 h-80 w-80 rounded-full bg-teal/30 blur-3xl" />
        <div className="absolute -top-16 -right-10 h-72 w-72 rounded-full bg-magenta/25 blur-3xl" />
        <div className="relative z-10 flex flex-col justify-between p-10 text-white w-full">
          <Logo size={60} onLight={false} linkTo={null} />
          <div>
            <h2 className="font-display text-5xl font-bold tracking-tight leading-tight">
              Welcome back to the <br />
              <span className="text-gold">InfiniteSkills Learning Portal</span>.
            </h2>
            <p className="mt-4 max-w-md text-white/85">
              Career Transformation Through Digital Skills — continue your learning journey with Prerna Maheshwari.
            </p>
          </div>
          <p className="text-xs text-white/60">Channel Partner – NVDigitalEdu</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center px-6 py-14 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo size={44} /></div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Sign in</h1>
          <p className="text-muted-foreground mt-2">Continue your career transformation.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" className="mt-1.5 rounded-xl h-11" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" data-testid="login-password"
                  type={show ? "text" : "password"} required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password" className="rounded-xl h-11 pr-10" />
                <button type="button" onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="login-remember" />
                Remember me
              </label>
              <Link to="/forgot-password" className="text-primary hover:underline">Forgot password?</Link>
            </div>
            {err && <p data-testid="login-error" className="text-sm text-destructive">{err}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading}
              className="w-full h-11 rounded-xl royal-gradient border-0 text-white btn-lift">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <p className="text-sm text-center text-muted-foreground mt-6">
            New to InfiniteSkills?{" "}
            <Link to="/register" className="text-primary font-semibold hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
